import React, { useState, useEffect } from 'react';
import { Switch } from 'react-native';
import {
  Box,
  Button,
  HStack,
  Icons,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  Text,
  VStack,
  Icon,
  Divider,
  ButtonText,
  useAlert,
  useToken,
} from '@tonic/central-specialties-ui-themed';
import { DB } from '@tonic/central-specialties-utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { EntityCardColumn } from '../../../../components/general/EntityCardExplorer/EntityCardColumnsView/EntityCardColumn/EntityCardColumn';
import { ItemNumberCard } from '../ManagePage';
import { TextFormField } from '../../../../components/general/Form/FormField/specific';

export type Crew = {
  id: string;
  name: string;
  isArchived: boolean;
};

interface ManageCrewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ManageCrewModal: React.FC<ManageCrewModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { alert } = useAlert();
  const $backgroundLight300 = useToken('colors', 'backgroundLight300');
  const $primary600 = useToken('colors', 'primary600');
  const $backgroundLight600 = useToken('colors', 'backgroundLight600');
  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null);
  const [mode, setMode] = useState<'add' | 'edit' | null>(null);
  const [retrieveArchived, setRetriveArchived] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    isArchived: boolean;
  }>({ name: '', isArchived: false });

  const handleCrewsArchiveToggle = () => {
    setRetriveArchived((prev) => !prev);
  };

  const {
    data: crewRequests,
    isLoading: isCrewsLoading,
    refetch: refetchCrews,
  } = useQuery({
    queryKey: ['crewRequests'],
    queryFn: async () =>
      await DB.GET('/news/crews', {
        params: { query: { includeArchived: retrieveArchived } },
      }).then((res) => {
        return res.data.crews;
      }),
  });

  const { mutateAsync: handleAddCrew } = useMutation({
    mutationFn: ({ data }: { data: { name: string } }) =>
      DB.POST('/news/crews', {
        body: data,
      })
        .then(({ response }) => {
          if (response.ok) {
            alert({
              status: 'success',
              message: 'Success! Crew Added.',
              dismissible: true,
              timeout: 5000,
            });
            refetchCrews();
          } else {
            alert({
              status: 'error',
              message: 'Something went wrong, could not add this crew',
              timeout: 10000,
            });
          }
        })
        .catch((err) => console.error(err)),
  });

  const { mutateAsync: handleEditCrew } = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name: string; isArchived: boolean };
    }) =>
      DB.PUT('/news/crews/{id}', {
        params: { path: { id } },
        body: data,
      })
        .then(({ response }) => {
          if (response.ok) {
            alert({
              status: 'success',
              message: 'Success! Crew Edit Saved.',
              dismissible: true,
              timeout: 5000,
            });
            refetchCrews();
          } else {
            alert({
              status: 'error',
              message: 'Something went wrong, could not edit this crew',
              timeout: 10000,
            });
          }
        })
        .catch((err) => console.error(err)),
  });

  const { mutateAsync: handleArchiveCrew } = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      DB.PUT('/news/crews/{id}/archive', {
        params: { path: { id } },
      })
        .then(({ response }) => {
          if (response.ok) {
            alert({
              status: 'success',
              message: 'Success! Crew Archive Status updated.',
              dismissible: true,
              timeout: 5000,
            });
            refetchCrews();
          } else {
            alert({
              status: 'error',
              message: 'Something went wrong, could not update this crew',
              timeout: 10000,
            });
          }
        })
        .catch((err) => console.error(err)),
  });

  useEffect(() => {
    if (selectedCrew) {
      setMode('edit');
      setFormData({
        name: selectedCrew.name,
        isArchived: selectedCrew.isArchived,
      });
    }
  }, [selectedCrew]);

  const handleAdd = () => {
    setSelectedCrew(null);
    setFormData({ name: '', isArchived: false });
    setMode('add');
  };

  const handleSubmit = async () => {
    if (mode === 'edit' && selectedCrew) {
      await handleEditCrew({ id: selectedCrew?.id, data: formData });
    } else if (mode === 'add') {
      await handleAddCrew({ data: formData });
    }

    if (formData?.isArchived) {
      handleArchiveCrew({ id: selectedCrew?.id });
    }
    // reset
    setMode(null);
    setSelectedCrew(null);
  };

  useEffect(() => {
    refetchCrews();
  }, [retrieveArchived, refetchCrews]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalBackdrop />
      <ModalContent
        borderRadius={0}
        minHeight={600}
        // w="$96"
        maxWidth={1000}
        minWidth={700}
      >
        <ModalHeader
          bg="$primary700"
          alignItems="center"
          justifyContent="space-between"
          py="$2"
          pl="$10"
          pr="$6"
        >
          <Text color="$white" size="lg">
            Manage Crews
          </Text>
          <HStack alignItems="center" gap="$2">
            <ModalCloseButton
              w="$8"
              justifyContent="center"
              alignItems="center"
            >
              <Icon color="$white" size="xl" as={Icons.X} />
            </ModalCloseButton>
          </HStack>
        </ModalHeader>
        <ModalBody
          w="$full"
          p="$4"
          my="$2"
          flex={1}
          justifyContent="space-between"
          flexDirection="column"
        >
          <HStack>
            <VStack
              flex={1}
              width={'$1/3'}
              minHeight={600}
              marginHorizontal={'$1'}
            >
              <HStack alignItems="center" p="$4" space="md">
                <Text size="md">Show Archived</Text>
                <Switch
                  value={retrieveArchived}
                  onValueChange={handleCrewsArchiveToggle}
                  style={{
                    transform: [{ scale: 0.65 }],
                  }}
                  ios_backgroundColor="#3e3e3e"
                  trackColor={{
                    false: $backgroundLight300,
                    true: $primary600,
                  }}
                  thumbColor={$backgroundLight600}
                />
              </HStack>
              <EntityCardColumn
                items={crewRequests}
                pending={isCrewsLoading}
                categoryName="Crews"
                CardComponent={({ data }: { data: Crew }) => (
                  <ItemNumberCard
                    number={
                      data.isArchived ? `${data.name} (ARCHIVED)` : data.name
                    }
                    onPress={() => setSelectedCrew(data)}
                    minWidth="$48"
                    isSelected={selectedCrew?.id === data.id}
                  />
                )}
                containerProps={{
                  maxWidth: '$full',
                  minWidth: '$56',
                }}
              />
              <Box px={4} py={3}>
                <Button onPress={handleAdd}>
                  <ButtonText>Add Crew</ButtonText>
                </Button>
              </Box>
            </VStack>
            <Divider subtle orientation="vertical" />
            <VStack
              flex={1}
              px={6}
              py={6}
              justifyContent="space-between"
              width={'$2/3'}
              minHeight={600}
              marginHorizontal={'$1'}
            >
              {mode ? (
                <VStack height={'$full'} justifyContent="space-between">
                  <Text mb={4} size="lg" fontWeight="bold" textAlign="center">
                    {mode === 'add' ? 'New Crew' : 'Edit Crew'}
                  </Text>

                  <VStack space={'sm'}>
                    <TextFormField
                      placeholder="Crew Name"
                      label={'Enter Crew Name'}
                      value={formData.name}
                      multiline={false}
                      onChange={(text) =>
                        setFormData((fd) => ({ ...fd, name: text }))
                      }
                      readonly={false}
                    />

                    <HStack
                      alignItems="center"
                      justifyContent="space-between"
                      marginLeft={'$2'}
                    >
                      <Text>Archived</Text>
                      <Switch
                        value={formData.isArchived}
                        onValueChange={(val) =>
                          setFormData((fd) => ({ ...fd, isArchived: val }))
                        }
                        ios_backgroundColor="#3e3e3e"
                        style={{
                          transform: [{ scale: 0.65 }],
                        }}
                        trackColor={{
                          false: $backgroundLight300,
                          true: $primary600,
                        }}
                        thumbColor={$backgroundLight600}
                      />
                    </HStack>
                  </VStack>
                  <HStack
                    justifyContent="space-between"
                    alignContent="space-between"
                  >
                    <Button
                      variant="outline"
                      onPress={() => {
                        setMode(null);
                        setSelectedCrew(null);
                      }}
                    >
                      <ButtonText>Cancel</ButtonText>
                    </Button>
                    <Button onPress={handleSubmit}>
                      <ButtonText>
                        {mode === 'add' ? 'Create' : 'Save'}
                      </ButtonText>
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <Text color="$neutral500" textAlign="center">
                  Select a crew on the left or tap “Add Crew”
                </Text>
              )}
            </VStack>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
